import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { CONTACT, IMAGES } from "../data";

const LINKS = [
  { label: "Home", to: "/", hash: "" },
  { label: "Services", to: "/", hash: "#services" },
  { label: "Recent Jobs", to: "/recent-jobs", hash: "" },
  { label: "Pricing", to: "/pricing", hash: "" },
  { label: "Contact", to: "/", hash: "#contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onHome = pathname === "/";
  const solid = scrolled || !onHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, l) => {
    setOpen(false);
    if (l.to !== "/") return; // route links handled by <Link>
    e.preventDefault();
    if (!l.hash) {
      if (onHome) window.scrollTo({ top: 0, behavior: "smooth" });
      else navigate("/");
      return;
    }
    if (onHome) {
      document.querySelector(l.hash)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/" + l.hash);
    }
  };

  return (
    <header
      data-testid="sticky-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-brand-bg/90 backdrop-blur-xl border-b border-brand-line shadow-[0_8px_30px_rgba(74,53,40,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-[72px]">
          <Link to="/" className="flex flex-col leading-none" data-testid="logo-link">
            <span className="text-[10px] tracking-widest text-brand-olive font-medium" dir="rtl">
              {CONTACT.blessing}
            </span>
            <span className={`font-display font-extrabold text-xl tracking-tight ${solid ? "text-brand-brown" : "text-[#FCFAF6]"}`}>
              ALL DONE <span className="text-brand-olive">HANDYMAN</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {LINKS.map((l) => (
              <Link
                key={l.label}
                to={l.to + l.hash}
                onClick={(e) => go(e, l)}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`text-sm font-medium transition-colors hover:text-brand-olive ${solid ? "text-brand-ink/80" : "text-[#FCFAF6]/90"}`}
              >
                {l.label}
              </Link>
            ))}
            <a
              href={CONTACT.phoneRaw}
              data-testid="header-call-button"
              className="inline-flex items-center gap-2 rounded-full bg-brand-olive px-5 py-2.5 text-sm font-semibold text-brand-bg hover:bg-brand-oliveDark hover:scale-105 transition-all"
            >
              <Phone size={16} /> Call Now
            </a>
          </nav>

          <button
            className={`md:hidden ${solid ? "text-brand-brown" : "text-[#FCFAF6]"}`}
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-brand-bg/95 backdrop-blur-xl border-t border-brand-line px-5 py-4 flex flex-col gap-3">
          {LINKS.map((l) => (
            <Link
              key={l.label}
              to={l.to + l.hash}
              onClick={(e) => go(e, l)}
              className="py-2 text-brand-ink font-medium"
            >
              {l.label}
            </Link>
          ))}
          <a
            href={CONTACT.phoneRaw}
            data-testid="mobile-call-button"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-olive px-5 py-3 font-semibold text-brand-bg"
          >
            <Phone size={16} /> {CONTACT.phoneDisplay}
          </a>
        </div>
      )}
    </header>
  );
};
